/**
 * ใบสั่งผลิตหนึ่งใบ — `GET /v1/production-orders/{id}`
 *
 * สามอย่างที่หน้าจอนี้ต้องทำให้ผู้ใช้แยกออกจากกันให้ได้:
 *
 * 1. **ใบนี้ผูกกับสูตรฉบับไหนแล้วหรือยัง** — ก่อนสั่งปล่อยไม่มีสูตรผูกอยู่เลย และ
 *    นั่นคือกติกา ไม่ใช่ข้อมูลที่หายไป หลังสั่งปล่อยคือสูตรฉบับที่ถูกตรึงไว้ถาวร
 *    (`BUSINESS_RULES.md` ข้อ 2)
 * 2. **ใครเปิดใบและใครสั่งปล่อย** — สองคนนี้ต้องไม่ใช่คนเดียวกัน (D-010) และเป็น
 *    ข้อมูลที่ผู้ใช้ต้องอ่านได้ ไม่ใช่แค่กฎที่เซิร์ฟเวอร์บังคับเงียบ ๆ
 * 3. **ปิดงาน / ปิดใบ / ยกเลิก** — `ProductionOrderTransitionPanel` (`WO-P1-M5-002`)
 *
 * ⚠️ ไม่มีปุ่มแก้ไขทั้งหน้าจอ: contract ของกลุ่มนี้ไม่มี `PATCH` เลย การแก้ร่างจึง
 * ยังไม่มีเส้นทาง และหน้าจอพูดเรื่องนี้ตรงจุดที่ผู้ใช้จะมองหาปุ่มแก้ไข
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ProductionOrderFailureNotice } from './ProductionOrderFailureNotice';
import { ProductionOrderReleasePanel } from './ProductionOrderReleasePanel';
import { ProductionOrderTransitionPanel } from './ProductionOrderTransitionPanel';
import { orderStatusLabel, orderStatusTone } from './productionOrderView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { orDash } from '../products/productView';
import {
  fetchProductionOrder,
  type ProductionOrderFailure,
} from '../../lib/productionOrders/client';
import { MANUAL_SOURCE_TYPE, type ProductionOrder } from '../../lib/productionOrders/types';

export interface ProductionOrderDetailViewProps {
  readonly orderId: string;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** flag `can_approve` ของ `PRODUCTION_ORDER` — ดู `ProductionOrderScreen` */
  readonly canApprove: boolean;
  /** complete ต้องใช้ EDIT (close/cancel ใช้ APPROVE) */
  readonly canEdit?: boolean;
  readonly listHref: string;
}

type DetailState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly order: ProductionOrder }
  | ProductionOrderFailure;

export function ProductionOrderDetailView({
  orderId,
  apiBaseUrl,
  fetchImpl,
  canApprove,
  canEdit = false,
  listHref,
}: ProductionOrderDetailViewProps) {
  const { pathname } = useLocation();
  const [state, setState] = useState<DetailState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });

    void fetchProductionOrder(orderId, {
      apiBaseUrl,
      fetchImpl,
      signal: controller.signal,
    }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setState(result.status === 'ready' ? { status: 'ready', order: result.order } : result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [orderId, apiBaseUrl, fetchImpl]);

  const order = state.status === 'ready' ? state.order : null;
  const snapshotTaken =
    order !== null && (order.bom_id !== null || order.routing_id !== null);

  return (
    <div className="cap-page" data-testid="production-order-detail">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title" data-testid="production-order-no">
            {order === null ? 'ใบสั่งผลิต' : orDash(order.order_no)}
          </h1>
          <p className="cap-page__sub">
            {order === null
              ? 'รายละเอียดของใบสั่งผลิตหนึ่งใบ'
              : `${orDash(order.product_code)} · โรงงาน ${orDash(order.plant_code)}`}
          </p>
        </div>
        <div className="cap-page__actions">
          {order === null ? null : (
            <StatusBadge tone={orderStatusTone(order.status)}>
              {orderStatusLabel(order.status)}
            </StatusBadge>
          )}
          <Link
            className="cap-button cap-button--ghost"
            data-testid="production-order-back"
            to={{ pathname, search: listHref }}
          >
            กลับไปหน้ารายการ
          </Link>
        </div>
      </div>

      {state.status === 'loading' ? (
        <div className="cap-notice" data-testid="production-order-detail-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดใบสั่งผลิต…</p>
        </div>
      ) : null}

      {state.status !== 'loading' && state.status !== 'ready' ? (
        <ProductionOrderFailureNotice failure={state} context="detail" />
      ) : null}

      {order === null ? null : (
        <>
          <section className="cap-meta" aria-label="ข้อมูลของใบสั่งผลิต">
            <div className="cap-meta__item">
              <div className="cap-meta__label">สถานะ (ข้อ 14)</div>
              <div className="cap-meta__value" data-testid="production-order-status">
                {orderStatusLabel(order.status)} ({order.status})
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">สินค้า</div>
              <div className="cap-meta__value cap-table__code">{orDash(order.product_code)}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">โรงงาน (D-004)</div>
              <div className="cap-meta__value cap-table__code">{orDash(order.plant_code)}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">จำนวนที่วางแผน</div>
              {/* NUMERIC เป็น string ตลอดเส้นทาง — ไม่จัดรูปแบบซ้ำที่นี่ */}
              <div className="cap-meta__value cap-table__code" data-testid="production-order-quantity">
                {orDash(order.planned_quantity)}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">เริ่มตามแผน</div>
              <div className="cap-meta__value cap-table__code">
                {orDash(order.planned_start_date)}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">สิ้นสุดตามแผน</div>
              <div className="cap-meta__value cap-table__code">
                {orDash(order.planned_end_date)}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">ที่มาของใบ (D-034)</div>
              <div className="cap-meta__value cap-table__code" data-testid="production-order-source">
                {orDash(order.source_type)}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">รหัสภายใน (production_order_id)</div>
              <div className="cap-meta__value cap-table__code">{order.production_order_id}</div>
            </div>
          </section>

          <section
            className="cap-card cap-card--table"
            aria-label="ผู้ดำเนินการตามเอกสาร"
            data-testid="production-order-actors"
          >
            <div className="cap-card__head">
              <h2 className="cap-card__title">
                ผู้ดำเนินการ (อ่านอย่างเดียว){' '}
                <InfoHint
                  label="เกี่ยวกับผู้ดำเนินการ"
                  text="ผู้สั่งปล่อยต้องไม่ใช่ผู้เปิดใบ (D-010) — เซิร์ฟเวอร์เป็นผู้บันทึกทั้งสองค่านี้จากผู้เรียกจริง หน้าจอส่งให้ไม่ได้"
                />
              </h2>
            </div>
            <div className="cap-meta cap-meta--flush">
              <div className="cap-meta__item">
                <div className="cap-meta__label">ผู้เปิดใบ (created_by_employee_id)</div>
                <div className="cap-meta__value cap-table__code" data-testid="production-order-creator">
                  {orDash(order.created_by_employee_id)}
                </div>
              </div>
              <div className="cap-meta__item">
                <div className="cap-meta__label">ผู้สั่งปล่อย (released_by_employee_id)</div>
                <div
                  className="cap-meta__value cap-table__code"
                  data-testid="production-order-releaser"
                >
                  {orDash(order.released_by_employee_id)}
                </div>
              </div>
              <div className="cap-meta__item">
                <div className="cap-meta__label">สร้างเมื่อ</div>
                <div className="cap-meta__value">{orDash(order.created_at)}</div>
              </div>
              <div className="cap-meta__item">
                <div className="cap-meta__label">แก้ไขล่าสุด</div>
                <div className="cap-meta__value">{orDash(order.updated_at)}</div>
              </div>
            </div>
          </section>

          <section
            className="cap-card cap-card--table"
            aria-label="สูตรที่ถูกตรึงไว้กับใบสั่งผลิต"
            data-testid="production-order-snapshot"
            data-snapshot={snapshotTaken ? 'taken' : 'none'}
          >
            <div className="cap-card__head">
              <h2 className="cap-card__title">
                สูตรที่ถูกตรึงไว้{' '}
                <InfoHint
                  label="เกี่ยวกับสูตรที่ถูกตรึงไว้"
                  text="บันทึกตอนสั่งปล่อย และไม่เปลี่ยนตาม revision ใหม่ที่ออกทีหลัง (ข้อ 2)"
                />
              </h2>
            </div>

            {!snapshotTaken ? (
              <div className="cap-card__foot" data-testid="production-order-snapshot-none">
                <p>
                  ใบนี้ยังไม่มีสูตรผูกอยู่ — <strong>เป็นเรื่องปกติของใบที่ยังไม่ถูกสั่งปล่อย</strong>{' '}
                  ไม่ใช่ข้อมูลที่หายไป การบังคับว่าต้องมี BOM และ Routing อยู่ที่ขั้นตอนสั่งปล่อย
                  ไม่ใช่ตอนเปิดใบ
                </p>
              </div>
            ) : (
              <>
                <div className="cap-meta cap-meta--flush">
                  <div className="cap-meta__item">
                    <div className="cap-meta__label">สูตรการผลิตที่ตรึงไว้ (bom_id)</div>
                    <div className="cap-meta__value cap-table__code" data-testid="production-order-bom-id">
                      {orDash(order.bom_id)}
                    </div>
                  </div>
                  <div className="cap-meta__item">
                    <div className="cap-meta__label">ขั้นตอนการผลิตที่ตรึงไว้ (routing_id)</div>
                    <div
                      className="cap-meta__value cap-table__code"
                      data-testid="production-order-routing-id"
                    >
                      {orDash(order.routing_id)}
                    </div>
                  </div>
                </div>

                {order.operations.length === 0 ? (
                  <div className="cap-card__foot" data-testid="production-order-operations-empty">
                    <p>
                      เซิร์ฟเวอร์ยังไม่ได้ส่งขั้นตอนที่ถูกคัดลอกมากับคำตอบนี้ — หน้าจอจะไม่เดารายการ
                      ขั้นตอนขึ้นมาเอง เพราะขั้นตอนที่หน้างานอ่านผิดคืองานที่ทำผิด
                    </p>
                  </div>
                ) : (
                  <div className="cap-table-wrap">
                    <table className="cap-table">
                      <caption className="cap-visually-hidden">
                        ขั้นตอนการผลิตที่ถูกคัดลอกมาเป็นงานของใบสั่งผลิตใบนี้ตอนสั่งปล่อย
                      </caption>
                      <thead>
                        <tr>
                          <th scope="col">ลำดับขั้นตอน</th>
                          <th scope="col">หน่วยผลิต</th>
                          <th scope="col">เครื่องมือ</th>
                          <th scope="col">เวลามาตรฐาน</th>
                          <th scope="col">เวลาตั้งเครื่อง</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.operations.map((operation) => (
                          <tr
                            key={operation.operation_seq}
                            data-testid={`production-order-operation-${operation.operation_seq}`}
                          >
                            <th scope="row" className="cap-table__row-head">
                              {operation.operation_seq}
                            </th>
                            <td className="cap-table__code">{orDash(operation.work_center_code)}</td>
                            <td className="cap-table__code">{orDash(operation.tool_code)}</td>
                            <td className="cap-table__code">{orDash(operation.std_time)}</td>
                            <td className="cap-table__code">{orDash(operation.setup_time)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </section>

          {/*
            แถบสั่งปล่อยถูกเรนเดอร์ทุกสถานะของใบเมื่อผู้ใช้มีสิทธิ์ — **ห้ามถอดออก
            ทันทีที่สถานะขยับ** ไม่งั้นผลของคำสั่งที่เพิ่งกด (ทั้งใบยืนยันว่าสำเร็จ
            และคำอธิบายว่าทำไมล้มเหลว) จะหายไปพร้อมกับแถบในเฟรมเดียวกัน แถบเป็น
            ผู้ตัดสินเองว่าจะเสนอปุ่มหรือจะอธิบายว่าทำไมไม่มีปุ่ม
          */}
          {canApprove ? (
            <ProductionOrderReleasePanel
              order={order}
              apiBaseUrl={apiBaseUrl}
              fetchImpl={fetchImpl}
              onOrderChanged={(next) => {
                setState({ status: 'ready', order: next });
              }}
            />
          ) : (
            <div className="cap-notice" data-testid="production-order-release-unavailable" role="status">
              <StatusBadge tone="neutral">ไม่มีคำสั่งสั่งปล่อยให้กด</StatusBadge>
              <h2 className="cap-notice__title">สิทธิ์ชุดนี้ไม่มีการสั่งปล่อยใบสั่งผลิต</h2>
              <p className="cap-notice__text">
                สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_approve</code>{' '}
                ของโมดูล <code className="cap-table__code">PRODUCTION_ORDER</code>{' '}
                หน้าจอนี้จึงไม่เปิดปุ่มให้กด — การไม่แสดงปุ่มเป็นเพียง UX เพื่อไม่ให้กดสิ่งที่จะถูก
                ปฏิเสธ ผู้ตัดสินจริงคือ backend ทุกคำขอเสมอ
              </p>
            </div>
          )}

          <ProductionOrderTransitionPanel
            order={order}
            canEdit={canEdit}
            canApprove={canApprove}
            apiBaseUrl={apiBaseUrl}
            fetchImpl={fetchImpl}
            onOrderChanged={(next) => {
              setState({ status: 'ready', order: next });
            }}
          />

          <p className="cap-notice__note" data-testid="production-order-no-edit">
            ไม่มีปุ่มแก้ไขในหน้าจอนี้{' '}
            <InfoHint
              label="ทำไมไม่มีปุ่มแก้ไข"
              text={
                <>
                  เพราะกลุ่มคำสั่งของใบสั่งผลิตไม่มี PATCH เลยตาม contract — ร่างที่
                  กรอกผิดต้องเปิดใบใหม่ และที่มาของใบถูกบังคับเป็น{' '}
                  <code className="cap-table__code">{MANUAL_SOURCE_TYPE}</code> เสมอใน
                  Phase 1
                </>
              }
            />
          </p>
        </>
      )}
    </div>
  );
}
